import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SeoHead } from "@/components/SeoHead";
import { useContent } from "@/context/ContentContext";

export default function NedenBiz() {
  const { t } = useTranslation();
  const { media } = useContent();
  const chapters = t("whyus.chapters", { returnObjects: true });

  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <div data-testid="neden-biz-page" className="py-20 md:py-28">
      <SeoHead pageKey="whyus" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HEADER */}
        <Reveal>
          <p className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-5">
            {t("whyus.overline")}
          </p>
          <h1 className="font-display font-bold text-[#1a3636] text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none max-w-4xl">
            {t("whyus.title")}
          </h1>
        </Reveal>

        {/* MANIFESTO CHAPTERS */}
        <div className="mt-20 space-y-16 md:space-y-24">
          {chapters.map((ch, i) => (
            <Reveal key={i}>
              <div
                data-testid={`whyus-chapter-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start border-t border-[#1a3636]/10 pt-10"
              >
                <div className="md:col-span-3">
                  <span className="font-display text-6xl md:text-7xl font-light text-[#2b8a7e]">
                    {ch.no}
                  </span>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-display text-2xl md:text-4xl font-semibold text-[#1a3636] tracking-tight mb-5">
                    {ch.title}
                  </h2>
                  <p className="text-lg md:text-xl text-[#1a3636]/65 leading-relaxed max-w-3xl">
                    {ch.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* FINAL PARALLAX HERO IMAGE */}
      <section className="mt-24 md:mt-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div
            ref={imgRef}
            className="relative rounded-[2rem] overflow-hidden h-[420px] md:h-[600px] border-4 border-white shadow-[0_20px_40px_rgb(26,54,54,0.12)]"
          >
            {/* ==== FINAL NATURE / HEALTH HERO IMAGE ==== */}
            <motion.img
              style={{ y: imgY, scale: 1.2 }}
              src={media.nature}
              alt={t("whyus.finalLabel")}
              data-testid="whyus-final-image"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a3636]/90 via-[#1a3636]/30 to-transparent" />
            <div className="absolute inset-0 flex items-end">
              <div className="p-8 md:p-16 max-w-3xl">
                <span className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-4 block">
                  {t("whyus.finalLabel")}
                </span>
                <p className="font-display text-2xl md:text-4xl font-medium text-[#f8faf9] tracking-tight leading-snug">
                  {t("whyus.finalQuote")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
